import { BasedAtomicModel } from '../_types/types';
import { WebComponent } from '@helpers/WebComponent';

declare namespace arjoJs {
  function isTablet(): boolean;
}

const getModifiers = (model: BasedAtomicModel, baseClass: string) => {
  const classes = [baseClass];

  model.globalModifier?.forEach((m) => classes.push(m));
  model.styleModifier?.forEach((m) => classes.push(baseClass + '--' + m));

  if (model.theme) {
    classes.push('arjo-theme-' + model.theme);
  }

  if (model.headerTheme) {
    classes.push('arjo-theme-header-' + model.theme);
  }

  if (model.buttonTheme) {
    classes.push('arjo-theme-button-' + model.theme);
  }

  return classes.join(' ');
};

const getCookie = (name: string) => {
  const cookies: { [name: string]: string } = {};

  document.cookie.split(';').forEach((cookie) => {
    const [name, value] = cookie.split('=');

    cookies[name.trim()] = value;
  });

  return cookies[name];
};

export const setCookie = (name: string, value: string, days?: number, domain?: string, path = '/', secure = true, sameSite = 'None') => {
  let expires = '';
  if (days) {
    let date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }

  let cookieString = `${name}=${encodeURIComponent(value)}${expires}; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += '; Secure';
  }

  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`;
  }

  document.cookie = cookieString;
};

const generatePagingData = (numberPages: number, currentPage: number, range = 5) => {
  const halfRange = Math.floor(range / 2);
  let start = 0,
    end = 0;

  if (currentPage - halfRange < 1) {
    start = 1;

    end = numberPages < range ? numberPages : range;
  } else if (numberPages - halfRange < currentPage) {
    start = numberPages - range < 1 ? 1 : numberPages - range + 1;

    end = numberPages;
  } else {
    start = currentPage - halfRange;

    end = currentPage + halfRange;
  }

  return Array.from({length: end - start + 1}, (_, i) => i + start);
};

const roundNumber = (num: number | boolean | undefined | string | null, numberDecimalDigit: number = 2) => {
  if (typeof num !== 'number') {
    return '';
  }

  return Number(num).toFixed(numberDecimalDigit);
};

export { getModifiers, getCookie, generatePagingData, roundNumber };

export function splitWords(s: string) {
  var re,
    match,
    output = [];
  re = /([A-Za-z]?)([a-z]+)/g;

  match = re.exec(s);
  while (match) {
    output.push([match[1].toUpperCase(), match[2]].join(''));
    match = re.exec(s);
  }

  return output;
}

export const pascalToString = (s: string) => {
  const stringSplit = splitWords(s);

  return stringSplit.join(' ');
};

export function setPositionSticky(element: HTMLElement | null, scrollY: number) {
  if (!arjoJs.isTablet() || !element) {
    return;
  }

  const minTop = window.innerHeight - 20 - (element.offsetHeight || 0);
  const maxTop =
    parseInt(document.body.style.getPropertyValue('--header-height') || document.documentElement.style.getPropertyValue('--header-height')) + 20;

  if (minTop > maxTop) {
    element?.style?.setProperty('--top', `${maxTop}px`);

    return;
  }

  const rect = element?.getBoundingClientRect();
  const scrollValue = window.scrollY - scrollY;
  const stickyPosition = rect.top - scrollValue;
  const top = stickyPosition < minTop ? minTop : stickyPosition > maxTop ? maxTop : stickyPosition;

  element?.style?.setProperty('--top', `${top}px`);

  return {
    scrollY: window.scrollY,
  };
}

export const viteAbsoluteUrl = (path: string, addExtension = false): string => {
  if (/^https?:\/\//gi.test(path)) {
    return path;
  }
  const baseUrl = import.meta.env.BASE_URL;
  const normalizedRemain =
    (path?.startsWith('/') ? path : '/' + path) + (addExtension && !path.endsWith('/') ? import.meta.env.VITE_PATH_EXTENSION : '');
  if (!baseUrl) {
    return normalizedRemain;
  }

  if (!baseUrl.endsWith('/')) {
    return baseUrl + normalizedRemain;
  }

  const len = baseUrl.length;
  return baseUrl.substring(0, len - 1) + normalizedRemain;
};

export const getMetaData = () => {
  return typeof metaData !== 'undefined' ? metaData : undefined;
}

export const dispatchCustomEvent = ({ eventName, eventDict }: { eventName: string; eventDict?: CustomEventInit }) => {
  const event = new CustomEvent(eventName, eventDict);

  document.dispatchEvent(event);
};

const getWebComponentRootNode = (tagName?: string) => {
  if (!tagName) return document;

  const element = document.querySelectorAll(tagName);

  if (!element?.length) return document;

  return element;
};

export const createWebComponentInstances = <T extends WebComponent>(ComponentFactory: new (...args: any[]) => T, componentName: string) => {
  let instances: T[] = [];

  const rootNode = getWebComponentRootNode(componentName);
  if (rootNode instanceof Document) {
    instances.push(new ComponentFactory(document));
  }

  return instances;
};

export const getOriginHref = () => {
  return `${location.origin}${location.pathname}`;
};
